import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Download } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useVendorStore from "../../HomePage/useVendorStore";
import { STATUS_BEGIN, STATUS_FAIL, PO_STATUS } from "../../../../utils/constants/others";
import getHexColor from "utils/getHexColor";
import {
  getPurchaseOrderEmptyStateDestination,
  getPurchaseOrderPageConfig,
  getPurchaseOrderStatusConfig,
  PURCHASE_ORDER_PAGE_CONFIG,
  PURCHASE_ORDER_PAGES,
  PURCHASE_ORDER_STATUSES,
  PO_LIST_PAGE_SIZE,
} from "./config";

const getStatusColors = (status) => {
  const config = getPurchaseOrderStatusConfig(status);
  const colorType = config.color || "grey";
  return {
    bgColor: getHexColor({ type: colorType, tint: 100 }),
    textColor: getHexColor({ type: colorType, tint: 600 }),
  };
};

const resolveEffectiveStatus = (row) => {
  const poStatus = row.po_status;
  if (poStatus === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL || poStatus === PO_STATUS.COMPLETED) {
    return poStatus;
  }
  return row.derived_status || poStatus;
};

const MIN_PURCHASE_ORDER_SEARCH_LENGTH = 3;
const PURCHASE_ORDER_ID_LENGTH = 7;

const getMinimumSearchLength = (searchTerm) =>
  /^\d+$/.test(searchTerm) ? PURCHASE_ORDER_ID_LENGTH : MIN_PURCHASE_ORDER_SEARCH_LENGTH;

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
};

const TAB_PARAM_TO_PAGE = {
  "1": PURCHASE_ORDER_PAGES.NEW,
  "2": PURCHASE_ORDER_PAGES.IN_PROGRESS,
  "3": PURCHASE_ORDER_PAGES.CLOSED,
};

export default function usePurchaseOrderListLogic() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const outletId = window.localStorage.getItem("outlet_id");
  const {
    getPurchaseOrderDetailsWithPeek,
    getPurchaseOrderPageCounts,
    resetPurchaseOrderDetails,
    getPurchaseOrderDetailsStatus,
    purchaseOrderDetails,
  } = useVendorStore();

  const [activePage, setActivePage] = useState(
    () => TAB_PARAM_TO_PAGE[searchParams.get("tab")] ?? PURCHASE_ORDER_PAGES.NEW
  );
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [appliedDeliveryDate, setAppliedDeliveryDate] = useState(null);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [appliedWarehouses, setAppliedWarehouses] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageCounts, setPageCounts] = useState({});
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const normalizedSearchQuery = searchQuery.trim();
  const debouncedSearchQuery = useDebouncedValue(normalizedSearchQuery, 400);
  const minimumSearchLength = getMinimumSearchLength(normalizedSearchQuery);
  const debouncedMinimumSearchLength = getMinimumSearchLength(debouncedSearchQuery);
  const isSearchReady = debouncedSearchQuery.length >= debouncedMinimumSearchLength;
  const isSearching = isSearchReady;
  const purchaseOrderSearchQuery = isSearching ? debouncedSearchQuery : "";
  const showSearchPrompt =
    searchVisible && (normalizedSearchQuery.length < minimumSearchLength || !isSearchReady);

  const activePageConfig = getPurchaseOrderPageConfig(activePage);
  const emptyStateDestination = getPurchaseOrderEmptyStateDestination(activePage, pageCounts);

  const fetchPurchaseOrders = useCallback(
    async (requestedPageNumber = 1, isFirstPage = true) => {
      let statuses = isSearching
        ? []
        : selectedStatuses.length > 0
          ? selectedStatuses
          : activePageConfig.statuses;

      // For CLOSED page, default to only EXPIRED if no filter is selected
      if (activePage === PURCHASE_ORDER_PAGES.CLOSED && selectedStatuses.length === 0 && !isSearching) {
        statuses = [PURCHASE_ORDER_STATUSES.EXPIRED];
      }

      let dateRangeParams = {};
      if (appliedDeliveryDate) {
        const base = new Date(appliedDeliveryDate);
        const pad = (n) => String(n).padStart(2, "0");
        const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const minus1 = new Date(base);
        minus1.setDate(base.getDate() - 1);
        const plus1 = new Date(base);
        plus1.setDate(base.getDate() + 1);
        dateRangeParams = {
          start_expected_delivery_date: fmt(minus1),
          end_expected_delivery_date: fmt(plus1),
        };
      }

      const warehouseParams = appliedWarehouses.length > 0
        ? { warehouse_code: appliedWarehouses }
        : {};

      const result = await getPurchaseOrderDetailsWithPeek(
        {
          statuses,
          po_status: isSearching
            ? `${PO_STATUS.APPROVED},${PO_STATUS.COMPLETED},${PO_STATUS.WAITING_FOR_VENDOR_APPROVAL},${PO_STATUS.PENDING_ADMIN_APPROVAL}`
            : `${PO_STATUS.APPROVED},${PO_STATUS.COMPLETED},${PO_STATUS.PENDING_ADMIN_APPROVAL}`,
          vendor_id: [outletId],
          purchase_order_number: purchaseOrderSearchQuery,
          page_no: requestedPageNumber,
          page_size: PO_LIST_PAGE_SIZE,
          ...dateRangeParams,
          ...warehouseParams,
        },
        isFirstPage
      );

      const listResponse = result?.listResponse ?? null;

      if (listResponse && selectedStatuses.length === 0 && !isSearching && !appliedDeliveryDate) {
        setPageCounts((counts) => ({
          ...counts,
          [activePage]: Number(listResponse.total_count ?? listResponse.order_count) || 0,
        }));
      }

      return result;
    },
    [
      activePage,
      activePageConfig.statuses,
      appliedDeliveryDate,
      appliedWarehouses,
      getPurchaseOrderDetailsWithPeek,
      isSearching,
      outletId,
      purchaseOrderSearchQuery,
      selectedStatuses,
    ]
  );

  useEffect(() => {
    setPageNumber(1);
    setHasNextPage(false);
    if (searchVisible && !isSearching) {
      resetPurchaseOrderDetails();
      return;
    }
    fetchPurchaseOrders(1, true).then((result) => {
      if (result) {
        setHasNextPage(result.hasNextPage);
      }
    });
  }, [fetchPurchaseOrders, isSearching, resetPurchaseOrderDetails, searchVisible]);

  useEffect(() => {
    let isMounted = true;
    const pageConfigsToPreload = PURCHASE_ORDER_PAGE_CONFIG.filter(
      (page) => page.key !== PURCHASE_ORDER_PAGES.NEW
    );

    getPurchaseOrderPageCounts(pageConfigsToPreload, [outletId]).then((counts) => {
      if (isMounted) {
        setPageCounts((currentCounts) => ({
          ...counts,
          ...currentCounts,
        }));
      }
    });

    return () => {
      isMounted = false;
    };
  }, [getPurchaseOrderPageCounts, outletId]);

  useEffect(
    () => () => {
      resetPurchaseOrderDetails();
    },
    [resetPurchaseOrderDetails]
  );

  const handlePageChange = (page) => {
    if (page === activePage) {
      return;
    }
    setSelectedStatuses([]);
    setActivePage(page);
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
    );
  };

  const toggleWarehouse = (warehouseCode) => {
    setSelectedWarehouses((current) =>
      current.includes(warehouseCode)
        ? current.filter((w) => w !== warehouseCode)
        : [...current, warehouseCode]
    );
  };

  const applyFilters = () => {
    setAppliedDeliveryDate(selectedDeliveryDate);
    setAppliedWarehouses(selectedWarehouses);
    setPageNumber(1);
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSelectedDeliveryDate(null);
    setAppliedDeliveryDate(null);
    setSelectedWarehouses([]);
    setAppliedWarehouses([]);
    setPageNumber(1);
  };

  const loadNextPage = async () => {
    if (getPurchaseOrderDetailsStatus === STATUS_BEGIN || !hasNextPage) {
      return;
    }

    const nextPage = pageNumber + 1;
    const result = await fetchPurchaseOrders(nextPage, false);
    if (result) {
      setPageNumber(nextPage);
      setHasNextPage(result.hasNextPage);
    }
  };

  const goToPage = async (requestedPageNumber) => {
    if (getPurchaseOrderDetailsStatus === STATUS_BEGIN || requestedPageNumber === pageNumber) {
      return;
    }

    const result = await fetchPurchaseOrders(requestedPageNumber, true);
    if (result) {
      setPageNumber(requestedPageNumber);
      setHasNextPage(result.hasNextPage);
    }
  };

  const handleDownload = (po) => {
    setToastMessage("");
    const documentUrl = po.po_document_url?.trim();
    if (!documentUrl) {
      setToastMessage("PO document is not available");
      return;
    }

    try {
      if (window?.downloadFileMessageHandler) {
        window.downloadFileMessageHandler.postMessage(documentUrl);
        return;
      }

      const downloadLink = document.createElement("a");
      downloadLink.href = documentUrl;
      downloadLink.target = "_blank";
      downloadLink.rel = "noopener noreferrer";
      downloadLink.download = `PO-${po.purchase_order_number}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
    } catch {
      setToastMessage("Something went wrong downloading the PO document");
    }
  };

  const handleOpen = (po, status) => {
    const source = status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL ? "list-pending" : "list";
    navigate(`/po/details/${source}/${po.id}`);
  };

  const handleContact = (po) => {
    setToastMessage("");
    const phoneNumber = String(po.created_by_phone_no || "").trim();
    if (!phoneNumber) {
      setToastMessage("Contact details are not available");
      return;
    }

    setSelectedContact({
      name: po.created_by_user_name || "PO creator",
      phoneNumber,
    });
  };

  const handleCall = () => {
    const formattedPhoneNumber = selectedContact?.phoneNumber.replace(/\s/g, "");
    if (formattedPhoneNumber) {
      window.location.href = `tel:+91${formattedPhoneNumber}`;
    }
  };

  const getPurchaseOrderActions = (item) => {
    const purchaseOrder = item;
    const documentUrl = purchaseOrder.po_document_url?.trim();
    return [
      documentUrl && {
        key: "download",
        label: `Download ${purchaseOrder.purchase_order_number}`,
        icon: <Download size={19} />,
        onClick: () => handleDownload(purchaseOrder),
      },
      {
        key: "open",
        label: `View ${purchaseOrder.purchase_order_number}`,
        icon: <ChevronRight size={20} />,
        onClick: () => handleOpen(purchaseOrder, purchaseOrder.po_status),
      },
    ].filter(Boolean);
  };

  const toggleSearch = () => {
    if (searchVisible) {
      setSearchQuery("");
    }
    setSearchVisible((visible) => !visible);
  };

  const isInitialLoading =
    getPurchaseOrderDetailsStatus === STATUS_BEGIN && pageNumber === 1 && purchaseOrderDetails.length === 0;
  const hasError = getPurchaseOrderDetailsStatus === STATUS_FAIL && purchaseOrderDetails.length === 0;

  const purchaseOrderTableRowData = useMemo(() => {
    return purchaseOrderDetails.map((row) => {
      const effectiveStatus = resolveEffectiveStatus(row);
      const { bgColor, textColor } = getStatusColors(effectiveStatus);
      const statusConfig = getPurchaseOrderStatusConfig(effectiveStatus);

      const netWeightRaw = (row.purchase_order_items || []).reduce(
        (total, item) => total + Number(item.quantity_ordered || 0) * Number(item.weight_per_packet || 0),
        0
      );
      const netWeight = `${netWeightRaw.toLocaleString("en-IN", { maximumFractionDigits: 2, useGrouping: false })} kg`;

      let deliverBy = "—";
      const deliveryDate = row.expected_delivery_date;
      if (deliveryDate) {
        const d = new Date(deliveryDate);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, "0");
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const month = monthNames[d.getMonth()];
          const year = String(d.getFullYear()).slice(-2);
          deliverBy = `${day} ${month} '${year}`;
        }
      }

      const poAmount = `₹${Number(row.po_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

      return {
        id: row.id,
        poStatus: row.po_status,
        poNumber: row.purchase_order_number,
        skuCount: (row.purchase_order_items || []).length,
        netWeight,
        deliverBy,
        poAmount,
        deliveryAt: row.warehouse_code
          ? `${row.warehouse_code}${row.warehouse_details?.city ? `, ${row.warehouse_details.city}` : ""}`
          : "—",
        statusLabel: statusConfig.label,
        statusTextColor: textColor,
        statusBgColor: bgColor,
        hasDocument: Boolean(row.po_document_url?.trim()),
        documentUrl: row.po_document_url || null,
        _raw: row,
      };
    });
  }, [purchaseOrderDetails]);

  return {
    activePage,
    selectedStatuses,
    setSelectedStatuses,
    toggleStatus,
    selectedDeliveryDate,
    setSelectedDeliveryDate,
    appliedDeliveryDate,
    selectedWarehouses,
    toggleWarehouse,
    applyFilters,
    clearFilters,
    pageNumber,
    hasNextPage,
    pageCounts,
    searchVisible,
    searchQuery,
    setSearchQuery,
    toastMessage,
    selectedContact,
    setSelectedContact,
    activePageConfig,
    emptyStateDestination,
    purchaseOrderDetails,
    purchaseOrderTableRowData,
    getPurchaseOrderDetailsStatus,
    isSearching,
    showSearchPrompt,
    minimumSearchLength,
    normalizedSearchQuery,
    isInitialLoading,
    hasError,
    handlePageChange,
    loadNextPage,
    goToPage,
    handleDownload,
    handleOpen,
    handleContact,
    handleCall,
    getPurchaseOrderActions,
    toggleSearch,
    fetchPurchaseOrders,
  };
}
